/**
 * Checkpoint Manager
 *
 * Service for capturing and restoring PowerPoint presentation state at specific points.
 * Used for debugging and replay functionality in the admin panel.
 */

import { createCheckpointApiClient } from '@carousel-office/shared';
import { isLoggingEnabled } from './config';
import { getOrCreatedocumentId } from '../../../services/documentIdManager';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface Checkpoint {
    checkpointId: string;
    sessionId: string;
    turnNumber: number;
    agentName: string;
    timestamp: string;
    host?: 'ppt' | 'excel';
    snapshotExtension?: string;
    snapshotSize?: number;
    documentId?: string;
    documentUnitCount?: number;
    // Legacy PPT metadata (kept for compatibility)
    slideCount?: number;
    pptxSize?: number;
}

export interface CheckpointWithData extends Checkpoint {
    snapshotBase64?: string;
    pptxBase64?: string;
}

interface SaveCheckpointRequest {
    sessionId: string;
    checkpointId: string;
    turnNumber: number;
    agentName: string;
    timestamp: string;
    host: 'ppt';
    snapshotBase64: string;
    snapshotExtension: 'pptx';
    documentId: string;
    documentUnitCount: number;
    // Legacy fields
    slideCount: number;
    pptxBase64: string;
}

const checkpointApiClient = createCheckpointApiClient();

// ═══════════════════════════════════════════════════════════════════════════
// CHECKPOINT MANAGER CLASS
// ═══════════════════════════════════════════════════════════════════════════

class CheckpointManager {
    private currentSessionId: string | null = null;
    private checkpointCount = 0;

    /**
     * Set the current session ID for checkpoint operations
     */
    setSession(sessionId: string): void {
        this.currentSessionId = sessionId;
        this.checkpointCount = 0;
    }

    /**
     * Clear the current session
     */
    clearSession(): void {
        this.currentSessionId = null;
        this.checkpointCount = 0;
    }

    /**
     * Capture the current presentation state and save as a checkpoint
     */
    async captureCheckpoint(
        turnNumber: number,
        agentName: string
    ): Promise<Checkpoint | null> {
        if (!isLoggingEnabled()) {
            console.log('[CheckpointManager] Logging disabled, skipping checkpoint');
            return null;
        }

        if (!this.currentSessionId) {
            console.warn('[CheckpointManager] No session set, cannot capture checkpoint');
            return null;
        }
        // Capture session ID at start to avoid race if session switches mid-capture.
        const sessionId = this.currentSessionId;

        try {
            console.log(`[CheckpointManager] Capturing checkpoint for agent: ${agentName}`);
            
            // Get deck ID
            const documentId = await getOrCreatedocumentId();
            
            // Capture presentation state
            const { pptxBase64, slideCount } = await this.capturePresentationState();
            
            if (!pptxBase64) {
                console.warn('[CheckpointManager] Failed to capture presentation state');
                return null;
            }

            // Generate checkpoint ID
            const timestamp = new Date().toISOString();
            const checkpointId = `checkpoint_${turnNumber}_${agentName}_${Date.now()}`;

            // Save to server
            const checkpoint = await this.saveCheckpoint({
                sessionId,
                checkpointId,
                turnNumber,
                agentName,
                timestamp,
                host: 'ppt',
                snapshotBase64: pptxBase64,
                snapshotExtension: 'pptx',
                documentId: documentId,
                documentUnitCount: slideCount,
                slideCount,
                pptxBase64,
            });

            if (checkpoint) {
                this.checkpointCount++;
                console.log(`[CheckpointManager] ✓ Checkpoint saved: ${checkpointId}`);
            }

            return checkpoint;
        } catch (error) {
            console.error('[CheckpointManager] Failed to capture checkpoint:', error);
            return null;
        }
    }

    /**
     * Capture the current presentation as base64 PPTX
     */
    private async capturePresentationState(): Promise<{ pptxBase64: string; slideCount: number }> {
        return new Promise((resolve, reject) => {
            try {
                // Get slide count first
                PowerPoint.run(async (context) => {
                    const slides = context.presentation.slides;
                    slides.load('items');
                    await context.sync();
                    
                    const slideCount = slides.items.length;
                    
                    // Get the file as compressed base64
                    Office.context.document.getFileAsync(
                        Office.FileType.Compressed,
                        { sliceSize: 4194304 }, // 4MB slices
                        async (result) => {
                            if (result.status === Office.AsyncResultStatus.Failed) {
                                console.error('[CheckpointManager] getFileAsync failed:', result.error);
                                resolve({ pptxBase64: '', slideCount: 0 });
                                return;
                            }

                            const file = result.value;
                            const slices: Uint8Array[] = [];

                            try {
                                // Read all slices
                                for (let i = 0; i < file.sliceCount; i++) {
                                    const sliceData = await this.getFileSlice(file, i);
                                    slices.push(sliceData);
                                }

                                // Combine binary slices and encode once as base64.
                                // Encoding per-slice can corrupt output due base64 padding boundaries.
                                const combinedData = this.encodeSlicesAsBase64(slices);
                                
                                file.closeAsync();
                                resolve({ pptxBase64: combinedData, slideCount });
                            } catch (sliceError) {
                                console.error('[CheckpointManager] Error reading file slices:', sliceError);
                                file.closeAsync();
                                resolve({ pptxBase64: '', slideCount: 0 });
                            }
                        }
                    );
                }).catch(reject);
            } catch (error) {
                reject(error);
            }
        });
    }

    /**
     * Get a single slice from the file
     */
    private getFileSlice(file: Office.File, sliceIndex: number): Promise<Uint8Array> {
        return new Promise((resolve, reject) => {
            file.getSliceAsync(sliceIndex, (result) => {
                if (result.status === Office.AsyncResultStatus.Failed) {
                    reject(result.error);
                    return;
                }

                // Convert slice data to raw bytes.
                const slice = result.value;
                const data = slice.data as unknown;
                
                // Office can return slice bytes in multiple forms depending on host/runtime.
                if (data instanceof ArrayBuffer) {
                    resolve(new Uint8Array(data));
                } else if (Array.isArray(data)) {
                    resolve(new Uint8Array(data));
                } else if (typeof data === 'string') {
                    // Defensive fallback for string-based payloads.
                    // Most environments should return ArrayBuffer/number[] for Compressed files.
                    try {
                        const binary = atob(data);
                        const bytes = new Uint8Array(binary.length);
                        for (let i = 0; i < binary.length; i++) {
                            bytes[i] = binary.charCodeAt(i);
                        }
                        resolve(bytes);
                    } catch {
                        const bytes = new Uint8Array(data.length);
                        for (let i = 0; i < data.length; i++) {
                            bytes[i] = data.charCodeAt(i) & 0xff;
                        }
                        resolve(bytes);
                    }
                } else {
                    reject(new Error(`[CheckpointManager] Unsupported slice data type at index ${sliceIndex}`));
                }
            });
        });
    }

    /**
     * Encode raw slice bytes into one contiguous base64 PPTX string.
     */
    private encodeSlicesAsBase64(slices: Uint8Array[]): string {
        if (slices.length === 0) {
            return '';
        }

        const totalLength = slices.reduce((sum, bytes) => sum + bytes.length, 0);
        const merged = new Uint8Array(totalLength);
        let offset = 0;
        for (const bytes of slices) {
            merged.set(bytes, offset);
            offset += bytes.length;
        }

        // Build binary in chunks to avoid spreading very large arrays.
        const chunkSize = 0x8000;
        let binary = '';
        for (let i = 0; i < merged.length; i += chunkSize) {
            const chunk = merged.subarray(i, i + chunkSize);
            binary += String.fromCharCode(...chunk);
        }
        return btoa(binary);
    }

    /**
     * Save checkpoint to the log server
     */
    private async saveCheckpoint(request: SaveCheckpointRequest): Promise<Checkpoint | null> {
        try {
            const response = await checkpointApiClient.createCheckpoint(
                request.sessionId,
                request as unknown as Record<string, unknown>
            );

            if (!response.ok) {
                const detail = await response.text().catch(() => '');
                console.warn(`[CheckpointManager] Failed to save checkpoint: ${response.status}${detail ? ` - ${detail}` : ''}`);
                return null;
            }

            const result = await response.json();
            if (result.success && result.data) {
                return {
                    checkpointId: request.checkpointId,
                    sessionId: request.sessionId,
                    turnNumber: request.turnNumber,
                    agentName: request.agentName,
                    timestamp: request.timestamp,
                    host: request.host,
                    snapshotExtension: request.snapshotExtension,
                    snapshotSize: request.snapshotBase64.length,
                    documentId: request.documentId,
                    documentUnitCount: request.documentUnitCount,
                    slideCount: request.slideCount,
                    pptxSize: request.pptxBase64.length,
                };
            }

            return null;
        } catch (error) {
            console.error('[CheckpointManager] Error saving checkpoint:', error);
            return null;
        }
    }

    /**
     * List checkpoints for a session
     */
    async listCheckpoints(sessionId: string): Promise<Checkpoint[]> {
        try {
            return await checkpointApiClient.listCheckpoints(sessionId);
        } catch (error) {
            console.error('[CheckpointManager] Error listing checkpoints:', error);
            return [];
        }
    }

    /**
     * Get a specific checkpoint with its PPTX data
     */
    async getCheckpoint(sessionId: string, checkpointId: string): Promise<CheckpointWithData | null> {
        try {
            const checkpoint = await checkpointApiClient.getCheckpoint(sessionId, checkpointId);
            if (!checkpoint) {
                return null;
            }

            // Ensure backward compatibility for older call sites.
            if (!checkpoint.snapshotBase64 && checkpoint.pptxBase64) {
                checkpoint.snapshotBase64 = checkpoint.pptxBase64;
            }
            if (!checkpoint.pptxBase64 && checkpoint.snapshotExtension === 'pptx' && checkpoint.snapshotBase64) {
                checkpoint.pptxBase64 = checkpoint.snapshotBase64;
            }
            return checkpoint;
        } catch (error) {
            console.error('[CheckpointManager] Error getting checkpoint:', error);
            return null;
        }
    }

    /**
     * Delete a checkpoint
     */
    async deleteCheckpoint(sessionId: string, checkpointId: string): Promise<boolean> {
        try {
            const response = await checkpointApiClient.deleteCheckpoint(sessionId, checkpointId);
            return response.ok;
        } catch (error) {
            console.error('[CheckpointManager] Error deleting checkpoint:', error);
            return false;
        }
    }

    /**
     * Get the checkpoint for a specific turn number.
     * Returns the checkpoint AT or BEFORE the given turn number.
     * This handles cases where not every turn has a checkpoint.
     * 
     * @param sessionId - The session ID
     * @param turnNumber - The turn number to find a checkpoint for
     * @returns The checkpoint with its PPTX data, or null if not found
     */
    async getCheckpointForTurn(sessionId: string, turnNumber: number): Promise<CheckpointWithData | null> {
        try {
            const checkpoints = await this.listCheckpoints(sessionId);
            
            if (checkpoints.length === 0) {
                console.log(`[CheckpointManager] No checkpoints found for session ${sessionId}`);
                return null;
            }
            
            // Find checkpoint with matching turnNumber, or closest preceding turn
            const matching = checkpoints
                .filter(cp => cp.turnNumber <= turnNumber)
                .sort((a, b) => b.turnNumber - a.turnNumber)[0];
            
            if (!matching) {
                console.log(`[CheckpointManager] No checkpoint at or before turn ${turnNumber}`);
                return null;
            }
            
            console.log(`[CheckpointManager] Found checkpoint ${matching.checkpointId} at turn ${matching.turnNumber} for requested turn ${turnNumber}`);
            return this.getCheckpoint(sessionId, matching.checkpointId);
        } catch (error) {
            console.error('[CheckpointManager] Error getting checkpoint for turn:', error);
            return null;
        }
    }
}

// ═══════════════════════════════════════════════════════════════════════════
// SINGLETON INSTANCE
// ═══════════════════════════════════════════════════════════════════════════

let checkpointManagerInstance: CheckpointManager | null = null;

export function getCheckpointManager(): CheckpointManager {
    if (!checkpointManagerInstance) {
        checkpointManagerInstance = new CheckpointManager();
    }
    return checkpointManagerInstance;
}

export { CheckpointManager };
